import type { Card } from "@memento/types/card";
import { type RenderConfigVideoClip } from "@memento/types/render-config";
import { Feature, canUseFeature } from "@memento/library/feature-access";
import { SectionLayout } from "src/components/Renderer/SectionLayout";
import { useEditor } from "src/components/Renderer/RendererProvider";
import { EditClipMusic } from "src/components/Renderer/Pages/EditClip/EditClipMusic";
import { EditClipCaption } from "src/components/Renderer/Pages/EditClip/EditClipCaption";
import { EditClipRotation } from "src/components/Renderer/Pages/EditClip/EditClipRotation";
import { EditVideoClipTrim } from "./EditVideoClipTrim";
import { EditClipSkipFace } from "src/components/Renderer/Pages/EditClip/EditClipSkipFace";
import { LockedTierBadge } from "src/components/LockedTierBadge";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";
import { useEditorClipSettingsDoneEvent } from "src/components/Renderer/Pages/EditClip/useEditorClipSettingsDoneEvent";

type EditClipProps = {
  card: Card;
  clip: RenderConfigVideoClip;
};

export const EditVideoClipPageId = "edit-video";

export const EditVideoClip = ({ card, clip }: EditClipProps) => {
  const { featureFlags } = useFeatureFlags();
  const { getRendererPageUrl, subscription } = useEditor();
  const { onClickDone } = useEditorClipSettingsDoneEvent(clip);

  const canEditCaption = canUseFeature(
    Feature.VideoClipCustomText,
    subscription,
    {
      card,
      featureFlags,
    }
  );

  return (
    <SectionLayout variant="non-scrolling">
      <SectionLayout.Header
        onDoneUrl={getRendererPageUrl({
          cardId: card.cardId,
          cardUrl: card.cardUrl,
          pageId: "clips",
        })}
        heading="Edit Video Clip"
        onClickDone={onClickDone}
      />
      <SectionLayout.NonScrollingContent>
        <EditVideoClipTrim clip={clip} />
        <SectionLayout.Separator />
        <SectionLayout.H2>
          {canEditCaption ? (
            "Caption"
          ) : (
            <div className="flex items-center gap-2">
              <LockedTierBadge feature={Feature.VideoClipCustomText} /> Caption
            </div>
          )}
        </SectionLayout.H2>
        <EditClipCaption clip={clip} card={card} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Rotate</SectionLayout.H2>
        <EditClipRotation clip={clip} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Music</SectionLayout.H2>
        <EditClipMusic clip={clip} />
        <SectionLayout.Separator />
        <SectionLayout.H2>Face Collage</SectionLayout.H2>
        <EditClipSkipFace clip={clip} />
      </SectionLayout.NonScrollingContent>
    </SectionLayout>
  );
};

type EditClipMobileFooterProps = {
  card: Card;
};

export const EditVideoClipMobileFooter = ({
  card,
  clip,
}: EditClipMobileFooterProps & { clip: RenderConfigVideoClip }) => {
  const { getRendererPageUrl } = useEditor();
  const { onClickDone } = useEditorClipSettingsDoneEvent(clip);

  return (
    <SectionLayout.MobileFooter
      onDoneUrl={getRendererPageUrl({
        cardId: card.cardId,
        cardUrl: card.cardUrl,
        pageId: "clips",
      })}
      onClickDone={onClickDone}
      className="md:hidden"
    />
  );
};
